import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  fetchAllPicks,
  fetchKOPicksSimple,
  submitPicks,
  updateGroupPicks as updateGroupPicksApi,
  updateKOPicks as updateKOPicksApi,
  updateKOMatchPick as updateKOMatchPickApi,
  updateThirdPlaceGroupPicks,
  updateTiebreakerAnswer as updateTiebreakerAnswerApi,
} from "../../api/picks";

const initialState = {
  loading: false,
  groupPicks: [],
  thirdPlacePicks: [],
  koPicks: [],
  koPicksSimple: {
    groupPicks: [],
    round32: [],
    round16: [],
    quarterFinal: [],
    semiFinal: [],
    finalists: [],
    final: [],
    thirdPlace: [],
  },
  tiebreakerAnswer: -1,
};

const getAllPicks = async (props, { rejectWithValue }) => {
  const { userId, contestId } = props;

  try {
    const res = await fetchAllPicks({
      user_id: userId,
      contest_id: contestId,
    });

    return res;
  } catch (err) {
    console.error("Error in [getAllPicks] action: ", err);

    return rejectWithValue(err?.response?.data);
  }
};

const getKnockoutPicksSimple = async (props, { rejectWithValue }) => {
  const { userId, contestId } = props;

  try {
    const res = await fetchKOPicksSimple({
      user_id: userId,
      contest_id: contestId,
    });

    return res;
  } catch (err) {
    console.error("Error in [getKnockoutPicksSimple] action: ", err);

    return rejectWithValue(err?.response?.data);
  }
};

export const updateGroupPicksAction = createAsyncThunk(
  "picks/updateGroupPicks",
  async (props, { rejectWithValue }) => {
    const { userId, contestId, groupName, rankings } = props;

    try {
      const res = await updateGroupPicksApi({
        user_id: userId,
        contest_id: contestId,
        group_name: groupName,
        rankings,
      });

      return res;
    } catch (err) {
      console.error("Error in [updateGroupPicks] action: ", err);

      return rejectWithValue(err?.response?.data);
    }
  },
);

export const updateThirdPlacePicksAction = createAsyncThunk(
  "picks/updateThirdPlacePicks",
  async (props, { rejectWithValue }) => {
    const { userId, contestId, rankings } = props;

    try {
      const res = await updateThirdPlaceGroupPicks({
        user_id: userId,
        contest_id: contestId,
        rankings,
      });

      return res;
    } catch (err) {
      console.error("Error in [updateThirdPlacePicks] action: ", err);

      return rejectWithValue(err?.response?.data);
    }
  },
);

export const updateKOPicksAction = createAsyncThunk(
  "picks/updateKOPicks",
  async (props, { rejectWithValue }) => {
    const { userId, contestId, teamNumber, round } = props;

    try {
      const res = await updateKOPicksApi({
        user_id: userId,
        contest_id: contestId,
        team_number: teamNumber,
        round_name: round,
      });

      return res;
    } catch (err) {
      console.error("Error in [updateKOPicks] action: ", err);

      return rejectWithValue(err?.response?.data);
    }
  },
);

export const updateKOMatchPickAction = createAsyncThunk(
  "picks/updateKOMatchPick",
  async (props, { rejectWithValue }) => {
    const { userId, contestId, matchNumber, winnerTeamNumber } = props;

    try {
      const res = await updateKOMatchPickApi({
        user_id: userId,
        contest_id: contestId,
        match_number: matchNumber,
        winner_team_number: winnerTeamNumber,
      });

      return res;
    } catch (err) {
      console.error("Error in [updateKOMatchPick] action: ", err);

      return rejectWithValue(err?.response?.data);
    }
  },
);

export const updateTiebreakerAnswerAction = createAsyncThunk(
  "picks/updateTiebreakerAnswer",
  async (props, { rejectWithValue }) => {
    const { userId, contestId, answer } = props;

    try {
      const res = await updateTiebreakerAnswerApi({
        user_id: userId,
        contest_id: contestId,
        tiebreaker_answer: answer,
      });

      return res;
    } catch (err) {
      console.error("Error in [updateTiebreakerAnswer] action: ", err);

      return rejectWithValue(err?.response?.data);
    }
  },
);

export const submitPicksAction = createAsyncThunk(
  "picks/submitPicks",
  async (props, { rejectWithValue }) => {
    const { userId, contestId } = props;

    try {
      const res = await submitPicks({
        user_id: userId,
        contest_id: contestId,
      });

      return res;
    } catch (err) {
      console.error("Error in [submitPicks] action: ", err);

      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getAllPicksAction = createAsyncThunk(
  "picks/getAllPicks",
  (props, api) => getAllPicks(props, api),
);
export const getKnockoutPicksSimpleAction = createAsyncThunk(
  "picks/getKnockoutPicksSimple",
  (props, api) => getKnockoutPicksSimple(props, api),
);

const picksSlice = createSlice({
  name: "picks",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getAllPicksAction.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllPicksAction.fulfilled, (state, action) => {
      const { knockout_simple } = action.payload;

      state.groupPicks = action.payload.group_picks;
      state.thirdPlacePicks = action.payload.third_place;
      state.koPicks = action.payload.knockout_picks;

      state.koPicksSimple.groupPicks = knockout_simple.group_picks;
      state.koPicksSimple.round32 = knockout_simple.round32;
      state.koPicksSimple.round16 = knockout_simple.round16;
      state.koPicksSimple.quarterFinal = knockout_simple.quarterFinal;
      state.koPicksSimple.semiFinal = knockout_simple.semiFinal;
      state.koPicksSimple.thirdPlace = knockout_simple.thirdPlace;
      state.koPicksSimple.final = knockout_simple.final;
      state.koPicksSimple.finalists = knockout_simple.finalists;

      state.loading = false;
    });

    builder.addCase(getKnockoutPicksSimpleAction.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getKnockoutPicksSimpleAction.fulfilled, (state, action) => {
      const { round_selections } = action.payload;

      state.koPicksSimple.groupPicks = round_selections.group_picks;
      state.koPicksSimple.round32 = round_selections.round32;
      state.koPicksSimple.round16 = round_selections.round16;
      state.koPicksSimple.quarterFinal = round_selections.quarterFinal;
      state.koPicksSimple.semiFinal = round_selections.semiFinal;
      state.koPicksSimple.thirdPlace = round_selections.thirdPlace;
      state.koPicksSimple.final = round_selections.final;
      state.koPicksSimple.finalists = round_selections.finalists;

      state.loading = false;
    });

    builder.addCase(getAllPicksAction.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(getKnockoutPicksSimpleAction.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(updateGroupPicksAction.fulfilled, (state, action) => {
      const { group_picks, third_place, knockout_picks, knockout_simple } =
        action.payload;

      state.groupPicks = group_picks;
      state.thirdPlacePicks = third_place;
      state.koPicks = knockout_picks;

      state.koPicksSimple.groupPicks = knockout_simple.group_picks;
      state.koPicksSimple.round32 = knockout_simple.round32;
      state.koPicksSimple.round16 = knockout_simple.round16;
      state.koPicksSimple.quarterFinal = knockout_simple.quarterFinal;
      state.koPicksSimple.semiFinal = knockout_simple.semiFinal;
      state.koPicksSimple.thirdPlace = knockout_simple.thirdPlace;
      state.koPicksSimple.final = knockout_simple.final;
      state.koPicksSimple.finalists = knockout_simple.finalists;
    });
    builder.addCase(updateThirdPlacePicksAction.fulfilled, (state, action) => {
      const { knockout_simple } = action.payload;

      state.thirdPlacePicks = action.payload.third_place;

      state.koPicksSimple.groupPicks = knockout_simple.group_picks;
      state.koPicksSimple.round32 = knockout_simple.round32;
      state.koPicksSimple.round16 = knockout_simple.round16;
      state.koPicksSimple.quarterFinal = knockout_simple.quarterFinal;
      state.koPicksSimple.semiFinal = knockout_simple.semiFinal;
      state.koPicksSimple.thirdPlace = knockout_simple.thirdPlace;
      state.koPicksSimple.final = knockout_simple.final;
      state.koPicksSimple.finalists = knockout_simple.finalists;
    });
    builder.addCase(updateKOPicksAction.fulfilled, (state, action) => {
      const { knockout_simple } = action.payload;

      state.koPicksSimple.groupPicks = knockout_simple.group_picks;
      state.koPicksSimple.round32 = knockout_simple.round32;
      state.koPicksSimple.round16 = knockout_simple.round16;
      state.koPicksSimple.quarterFinal = knockout_simple.quarterFinal;
      state.koPicksSimple.semiFinal = knockout_simple.semiFinal;
      state.koPicksSimple.thirdPlace = knockout_simple.thirdPlace;
      state.koPicksSimple.final = knockout_simple.final;
      state.koPicksSimple.finalists = knockout_simple.finalists;
    });
    builder.addCase(updateKOMatchPickAction.fulfilled, (state, action) => {
      const { knockout_picks, knockout_simple } = action.payload;

      // per-match winners (drives the bracket selections)
      if (knockout_picks) state.koPicks = knockout_picks;

      // keep the simple buckets in sync too (used elsewhere / scoring read path)
      if (knockout_simple) {
        state.koPicksSimple.groupPicks = knockout_simple.group_picks;
        state.koPicksSimple.round32 = knockout_simple.round32;
        state.koPicksSimple.round16 = knockout_simple.round16;
        state.koPicksSimple.quarterFinal = knockout_simple.quarterFinal;
        state.koPicksSimple.semiFinal = knockout_simple.semiFinal;
        state.koPicksSimple.thirdPlace = knockout_simple.thirdPlace;
        state.koPicksSimple.final = knockout_simple.final;
        state.koPicksSimple.finalists = knockout_simple.finalists;
      }
    });
    builder.addCase(updateTiebreakerAnswerAction.fulfilled, (state, action) => {
      state.tiebreakerAnswer = action.payload.tiebreaker_answer;
    });
  },
});

export default picksSlice.reducer;
